import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables/useRest";

export const useJuzgadosStore = defineStore("juzgados", () => {
  const juzgados = ref([]);
  const allJuzgados = ref([]);
  const allJuzgadosListLoad = ref(false);
  const catalogNeedsRefresh = ref(false);
  const isLoading = ref(false);
  const pageSize = ref(6);
  const currentPage = ref(1);
  const totalJuzgados = ref(0);

  const rest = useRest();

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);

  const totalPages = computed(() => Math.ceil(totalJuzgados.value / pageSize.value));

  async function getTotal(searchQuery = null) {
    try {
      let url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/juzgado/?getTotal=true`;
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const { data } = await rest.get({ url });
      totalJuzgados.value = +data.doc.total;
    } catch (e) {
      console.error("getTotal>>", e);
    }
  }

  async function setAllJuzgados(refresh = false) {
    try {
      const shouldRefresh = refresh || catalogNeedsRefresh.value;

      if (allJuzgadosListLoad.value && !shouldRefresh) {
        return allJuzgados.value;
      }

      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/juzgado/`;
      const r = await rest.get({ url });
      const datos = r.data.data;

      allJuzgados.value = datos;
      allJuzgadosListLoad.value = true;
      catalogNeedsRefresh.value = false;

      return allJuzgados.value;
    } catch (e) {
      console.error("setAllJuzgados>>", e);
      return allJuzgados.value;
    }
  }

  async function setJuzgados(page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;

      const limit = pageSize.value;
      const offsetValue = (page - 1) * limit;

      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/juzgado/?limit=${limit}&offset=${offsetValue}`;
      const r = await rest.get({ url });

      juzgados.value = r.data.data;
      isLoading.value = false;
      return juzgados.value;
    } catch (e) {
      console.error("setJuzgados>>", e);
      isLoading.value = false;
      return juzgados.value;
    }
  }

  async function searchJuzgados(searchQuery, page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;

      const limit = pageSize.value;
      const offsetValue = (page - 1) * limit;

      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/juzgado/?limit=${limit}&offset=${offsetValue}&search=${encodeURIComponent(searchQuery)}`;
      const r = await rest.get({ url });

      juzgados.value = r.data.data;
      isLoading.value = false;
    } catch (e) {
      console.error("searchJuzgados>>", e);
      isLoading.value = false;
    }
  }

  async function createJuzgado(payload) {
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/juzgado`;
      const { data } = await rest.post({ url, formData: payload });
      allJuzgadosListLoad.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("createJuzgado>>", e);
      throw e;
    }
  }

  async function updateJuzgado(id, payload) {
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/juzgado/${id}`;
      const { data } = await rest.put({ url, formData: payload });
      allJuzgadosListLoad.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("updateJuzgado>>", e);
      throw e;
    }
  }

  async function deleteJuzgado(id) {
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/juzgado/${id}`;
      const { data } = await rest.del({ url });
      allJuzgadosListLoad.value = false;
      catalogNeedsRefresh.value = true;
      await getTotal();
      await setJuzgados(currentPage.value);
      return data;
    } catch (e) {
      console.error("deleteJuzgado>>", e);
      throw e;
    }
  }

  function handlePageChange(event) {
    const page = Math.floor(event.first / pageSize.value) + 1;
    goToPage(page);
  }

  function goToPage(page) {
    currentPage.value = page;
    setJuzgados(page);
  }

  function prevPage() {
    if (currentPage.value > 1) {
      goToPage(currentPage.value - 1);
    }
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) {
      goToPage(currentPage.value + 1);
    }
  }

  return {
    juzgados,
    allJuzgados,
    isLoading,
    pageSize,
    currentPage,
    offset,
    totalJuzgados,
    totalPages,
    setAllJuzgados,
    setJuzgados,
    searchJuzgados,
    getTotal,
    createJuzgado,
    updateJuzgado,
    deleteJuzgado,
    handlePageChange,
    goToPage,
    prevPage,
    nextPage,
  };
});
